import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Directive, ElementRef, EventEmitter, forwardRef, HostListener, Inject, Input, OnChanges, Optional, Output, Renderer2, SimpleChanges } from '@angular/core';
import { COMPOSITION_BUFFER_MODE, ControlValueAccessor, NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskedInputDirective, TextMaskConfig } from 'angular2-text-mask';
import KeyDownEvent = JQuery.KeyDownEvent;

@Directive({
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    // NOTE: we have to do it this way because of MaskedInputDirective
    host: {
        '(input)': 'handleInput($event.target.value)',
        '(focus)': 'onFocus()',
        '(blur)': 'onBlur()',
        '(compositionstart)': '_compositionStart()',
        '(compositionend)': '_compositionEnd($event.target.value)'
    },
    selector: '[ngModel][numberMask]',
    exportAs: 'numberMask',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberMaskDirective),
            multi: true
        }
    ]
})
export class NumberMaskDirective extends MaskedInputDirective implements ControlValueAccessor, OnChanges {

    // Interface Properties
    @Input() numberMask: string;
    @Input() prefix: string;
    @Input() formatWhileTyping = false;
    @Input() allowLeadingZeroes = true;
    @Input() allowNegativeNumbers = false;
    @Input() allowOverriding = true;
    @Output() numberValue = new EventEmitter<number>();

    // Local Properties
    boundaries = {
        minimumIntegers: 1,
        maximumIntegers: -1,
        minimumDecimals: 0,
        maximumDecimals: -1
    }
    focused = false;

    // Constructor
    constructor(
        private el: ElementRef,
        _renderer: Renderer2,
        @Optional() @Inject(COMPOSITION_BUFFER_MODE) _compositionMode: boolean
    ) {
        super(_renderer, el, _compositionMode);
    }

    // Event Functions
    ngOnChanges(changes: SimpleChanges) {

        // Extracting the relevant information
        const minIntegersMatch = this.numberMask.match(/^\d+[.-]/);
        if (minIntegersMatch && minIntegersMatch.length > 0) {
            this.boundaries.minimumIntegers = Number.parseInt(minIntegersMatch[0].slice(0, minIntegersMatch[0].length - 1), 10);
        }
        const maxIntegersMatch = this.numberMask.match(/-\d+[.-]/);
        if (maxIntegersMatch && maxIntegersMatch.length > 0) {
            this.boundaries.maximumIntegers = Number.parseInt(maxIntegersMatch[0].slice(1, maxIntegersMatch[0].length - 1), 10);
        }
        const minDecimalMatch = this.numberMask.match(/\.\d+/);
        if (minDecimalMatch && minDecimalMatch.length > 0) {
            this.boundaries.minimumDecimals = Number.parseInt(minDecimalMatch[0].slice(1, minDecimalMatch[0].length), 10);
        }
        const maxDecimalMatch = this.numberMask.match(/-\d+$/);
        if (maxDecimalMatch && maxDecimalMatch.length > 0) {
            this.boundaries.maximumDecimals = Number.parseInt(maxDecimalMatch[0].slice(1, maxDecimalMatch[0].length), 10);
        }

        // Constructing our mask
        this.textMaskConfig = this.getNumberMaskConfig();
        super.ngOnChanges(changes);
    }

    onFocus() {
        this.focused = true;
        this.textMaskConfig = this.getNumberMaskConfig();
        this._setupMask(true);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyDownEvent) {

        // Prevents overrides if we don't allow them
        if (!this.allowOverriding) {

            // Obtaining the new value
            let current: string = this.el.nativeElement.value;
            const indexStart = event.target.selectionStart;
            const indexEnd = event.target.selectionEnd;
            const key = event.key;
            let next: string = current.slice(0, indexStart) + key + current.slice(indexEnd + Math.abs(0));

            // Checking if we're beyond the max
            const components = this.extractComponents(next);
            if (
                (this.boundaries.maximumIntegers >= 0 && components[0].length > this.boundaries.maximumIntegers)
                || (this.boundaries.maximumDecimals >= 0 && components[1].length > this.boundaries.maximumDecimals)
            ) {
                event.preventDefault();
            }
        }
    }

    handleInput(value: string) {

        // Pushing the new avlue
        this._handleInput(value);

        // Outputting the number value (not the masked value)
        const numberMatch = value.match(/^.*\d+.?\d*$/);
        if (numberMatch) {
            this.numberValue.emit(parseFloat(numberMatch[0]));
        } else {
            this.numberValue.emit(null);
        }
    }

    onBlur() {

        // Calling the onTouched handler from angular7 text mask
        this.onTouched();

        // Sets up the value to contain the right number of integers (specifically so it doesn't move the decimal)
        let value = this.el.nativeElement.value;
        const components = this.extractComponents(value);
        for (let i = components[0].length; i < this.boundaries.minimumIntegers; i++) {
            value = '0' + value;
        }

        // Writing the new value with our enforced mask
        this.focused = false;
        this.textMaskConfig = this.getNumberMaskConfig();
        this._setupMask(true);
        this.writeValue(value);
    }

    getNumberMaskConfig(): TextMaskConfig {
        // POST: sets up the text mask config for the directive

        // We enforce while we aren't typing OR if we're always enforcing
        if (
            (
                !this.focused
                || this.formatWhileTyping
            )
            && this.numberMask && this.numberMask.match(/\d+(-\d+)?(.\d+(-\d)?)?/)
        ) {

            // Constructing a mask function for dynamic scaling
            const maskFunction = (rawValue: string): Array<string | RegExp> => {

                // Extracting the components for mask construction
                const components = this.extractComponents(rawValue);

                // If the mask has prefixes, the use those
                const mask: Array<string | RegExp> = [];
                if (this.prefix) {
                    for (let i = 0; i < this.prefix.length; i++) {
                        mask.push(this.prefix[i]);
                    }
                }

                // Constructing integer mask
                for (
                    let i = 0;
                    (
                        (i < this.boundaries.minimumIntegers || i < components[0].length)
                        && (this.boundaries.maximumIntegers < 0 || i < this.boundaries.maximumIntegers)
                    );
                    i++
                ) {
                    mask.push(/\d/);
                }

                // Decimal masks are constructed only if we can have decimals
                if (this.boundaries.maximumDecimals != 0) {

                    // Adding in decimal
                    // If there are two periods, we want to put the cursor past the period
                    if (this.boundaries.minimumDecimals > 0 || rawValue.includes('.')) {
                        if (rawValue.includes('..')) {
                            mask.push('[]', '.', '[]');
                        } else {
                            mask.push('.', '[]');
                        }
                    }

                    // Constructing masks decimals
                    for (
                        let i = 0;
                        (
                            (i < this.boundaries.minimumDecimals || i < components[1].length)
                            && (this.boundaries.maximumDecimals < 0 || i < this.boundaries.maximumDecimals)
                        );
                        i++
                    ) {
                        mask.push(/\d/);
                    }
                }

                // If we allow negative numbers, we need to append a hyphen to our input when it is present
                if (this.allowNegativeNumbers && rawValue.includes('-')) {
                    mask.unshift('-');
                }

                // Returning the mask
                return mask;
            };

            // Returning our standardized text mask
            return {
                guide: true,
                mask: maskFunction,
                placeholderChar:
                    (
                        this.el.nativeElement
                        && (!this.el.nativeElement.value || parseInt(this.el.nativeElement.value, 10) != NaN)
                        && (!this.el.nativeElement.value || this.el.nativeElement.value.toString() == '0')
                        && this.boundaries.minimumIntegers <= 1
                        && this.boundaries.minimumDecimals <= 0
                    ) ? '_' : '0',
                showMask:
                    (
                        this.el.nativeElement
                        && this.el.nativeElement.value != null
                        && this.el.nativeElement.value != ''
                    )
            };
        }

        // Usually, we don't want to enforce the minimums while we're typing
        else {
            return {
                guide: false,
                mask: (rawValue: string) => {

                    // If we have two decimals, ignore the second one
                    let maskedValue = rawValue;
                    if (maskedValue.split('.').length > 2) {
                        const lastDecimalIndex = maskedValue.lastIndexOf('.');
                        maskedValue = maskedValue.slice(0, lastDecimalIndex) + maskedValue.slice(lastDecimalIndex + 1);
                    }

                    // Create the mask based on the adjusted value
                    return createNumberMask({
                        prefix: this.prefix ?? '',
                        includeThousandsSeparator: false,
                        integerLimit: this.boundaries.maximumIntegers >= 0 ? this.boundaries.maximumIntegers : null,
                        decimalLimit: this.boundaries.maximumDecimals >= 0 ? this.boundaries.maximumDecimals : null,
                        allowDecimal: this.boundaries.maximumDecimals != 0,
                        allowLeadingZeroes: this.allowLeadingZeroes,
                        allowNegative: this.allowNegativeNumbers
                    })(maskedValue);
                }
            };
        }
    }

    extractComponents(value: string): string[] {
        // PRE: a value that is a number
        // POST: extracts the numeric components
        const numberRegexp = /\d+/;
        let integerValue = '';
        const integerRegexp = /^[^\.\d]*\d+\.?/;
        const integerMatches = value.match(integerRegexp);
        if (integerMatches && integerMatches.length > 0) {
            integerValue = numberRegexp.exec(integerMatches[0])[0];
        }
        let decimalValue = '';
        const decimalRegexp = /\.\d+$/g;
        const decimalMatches = value.match(decimalRegexp);
        if (decimalMatches && decimalMatches.length > 0) {
            decimalValue = numberRegexp.exec(decimalMatches[0])[0];
        }
        return [integerValue, decimalValue];
    }
}
